/**
 * WHATSAPP MESSAGE MAPPERS — conversione tra tipi UI e DTO REST.
 *
 * Sola lettura + invio: il DTO REST è read-only (`WhatsAppMessage`), mentre
 * l'invio usa `WhatsAppMessageInput` (personId, phoneNumber, body).
 */

import { pickAuditFields } from '@crm/shared';
import { extractError } from '../../store/thunkHelpers';
import type {
  WhatsAppMessage,
  WhatsAppMessageDirection,
  WhatsAppMessageSendPayload,
  WhatsAppMessageStatus,
  WhatsAppMessageType,
  WhatsAppNumber,
  WhatsAppSendError,
  WhatsAppTemplate,
  WhatsAppTemplateSendPayload,
  WhatsAppWindowConflict,
} from './whatsAppMessageTypes';

/**
 * Shape del WhatsAppMessage come esce dal REST
 * (/o/crm/v1_0/whatsapp-message/{id}). Rispecchia lo schema WhatsAppMessage
 * nel rest-openapi.yaml.
 */
export interface WhatsAppMessageDto {
  whatsAppMessageId: number;
  companyId: number;
  groupId: number;
  userId: number;
  userName: string;
  createDate: string;
  modifiedDate: string;
  direction?: string;
  externalId?: string;
  phoneNumber?: string;
  senderName?: string;
  phoneNumberId?: string;
  type?: string;
  body?: string;
  mediaMimeType?: string;
  mediaFileName?: string;
  voiceNote?: boolean;
  receiptDate?: string;
  status?: string;
  statusDate?: string;
  errorCode?: number;
  errorMessage?: string;
  transcription?: string;
  transcriptionSummary?: string;
  transcriptionActionItems?: string;
  transcriptionDate?: string;
}

/** DTO -> modello UI. */
export function whatsAppMessageFromDto(dto: WhatsAppMessageDto): WhatsAppMessage {
  return {
    whatsAppMessageId: dto.whatsAppMessageId,
    ...pickAuditFields(dto),
    direction: (dto.direction as WhatsAppMessageDirection) ?? 'IN',
    externalId: dto.externalId ?? '',
    phoneNumber: dto.phoneNumber ?? '',
    senderName: dto.senderName ?? '',
    phoneNumberId: dto.phoneNumberId ?? '',
    // I messaggi pre-media non hanno type: default TEXT (retrocompat).
    type: (dto.type as WhatsAppMessageType) ?? 'TEXT',
    body: dto.body ?? '',
    mediaMimeType: dto.mediaMimeType ?? undefined,
    mediaFileName: dto.mediaFileName ?? undefined,
    // Default false, non undefined: l'assenza del flag significa "file audio", non
    // "non lo sappiamo" — vale per gli altri media e per gli audio ricevuti prima
    // che il flag venisse salvato.
    voiceNote: dto.voiceNote ?? false,
    receiptDate: dto.receiptDate ?? '',
    // Assente sugli IN (non pertinente) e sui messaggi antecedenti alla
    // funzionalità (stato di consegna sconosciuto): resta undefined, e la UI
    // semplicemente non mostra le spunte.
    status: (dto.status as WhatsAppMessageStatus) ?? undefined,
    statusDate: dto.statusDate ?? undefined,
    errorCode: dto.errorCode ?? undefined,
    errorMessage: dto.errorMessage ?? undefined,
    transcription: dto.transcription ?? undefined,
    transcriptionSummary: dto.transcriptionSummary ?? undefined,
    transcriptionActionItems: dto.transcriptionActionItems ?? undefined,
    transcriptionDate: dto.transcriptionDate ?? undefined,
  };
}

/**
 * Modello UI -> DTO REST. Usato dai mock MSW per simulare la shape del
 * backend; non invocato dall'app reale.
 */
export function whatsAppMessageToDto(m: WhatsAppMessage): WhatsAppMessageDto {
  return {
    whatsAppMessageId: m.whatsAppMessageId,
    ...pickAuditFields(m),
    direction: m.direction,
    externalId: m.externalId,
    phoneNumber: m.phoneNumber,
    senderName: m.senderName,
    phoneNumberId: m.phoneNumberId,
    type: m.type,
    body: m.body,
    mediaMimeType: m.mediaMimeType,
    mediaFileName: m.mediaFileName,
    voiceNote: m.voiceNote,
    receiptDate: m.receiptDate,
    status: m.status,
    statusDate: m.statusDate,
    errorCode: m.errorCode,
    errorMessage: m.errorMessage,
    transcription: m.transcription,
    transcriptionSummary: m.transcriptionSummary,
    transcriptionActionItems: m.transcriptionActionItems,
    transcriptionDate: m.transcriptionDate,
  };
}

/** Shape del payload accettato dal backend in POST .../send. */
export interface WhatsAppMessageInput {
  personId: number;
  phoneNumber: string;
  body: string;
  phoneNumberId?: string;
}

/** Payload invio UI -> DTO input. */
export function whatsAppMessageSendToInput(
  p: WhatsAppMessageSendPayload,
): WhatsAppMessageInput {
  return {
    personId: p.personId,
    phoneNumber: p.phoneNumber,
    body: p.body,
    phoneNumberId: p.phoneNumberId,
  };
}

/**
 * Shape del numero mittente come esce dal REST (GET /whatsapp-numbers/{groupId}).
 * Rispecchia lo schema WhatsAppNumber del rest-openapi.yaml.
 */
export interface WhatsAppNumberDto {
  whatsAppNumberId?: number;
  phoneNumberId?: string;
  phoneNumber?: string;
  label?: string;
}

export function whatsAppNumberFromDto(dto: WhatsAppNumberDto): WhatsAppNumber {
  return {
    whatsAppNumberId: dto.whatsAppNumberId ?? 0,
    phoneNumberId: dto.phoneNumberId ?? '',
    phoneNumber: dto.phoneNumber ?? '',
    label: dto.label ?? '',
  };
}

/** Shape del template come esce dal REST (GET /whatsapp-templates/{groupId}). */
export interface WhatsAppTemplateDto {
  name?: string;
  language?: string;
  category?: string;
  body?: string;
  parameterCount?: number;
}

export function whatsAppTemplateFromDto(
  dto: WhatsAppTemplateDto,
): WhatsAppTemplate {
  return {
    name: dto.name ?? '',
    language: dto.language ?? '',
    category: dto.category ?? '',
    body: dto.body ?? '',
    parameterCount: dto.parameterCount ?? 0,
  };
}

/** Shape del payload accettato dal backend in POST .../send-template. */
export interface WhatsAppTemplateMessageInput {
  personId: number;
  phoneNumber: string;
  templateName: string;
  language: string;
  parameters: string[];
  phoneNumberId?: string;
}

export function whatsAppTemplateSendToInput(
  p: WhatsAppTemplateSendPayload,
): WhatsAppTemplateMessageInput {
  return {
    personId: p.personId,
    phoneNumber: p.phoneNumber,
    templateName: p.templateName,
    language: p.language,
    parameters: p.parameters,
    phoneNumberId: p.phoneNumberId,
  };
}

/**
 * Traduce l'errore HTTP dell'invio in un {@link WhatsAppSendError}, riconoscendo
 * il 409 di finestra chiusa.
 *
 * Serve perché `extractError` appiattisce tutto a stringa e perderebbe lo status:
 * senza questo, un rifiuto per finestra chiusa diventerebbe un toast rosso
 * indistinguibile da un guasto, invece dell'invito a usare un template.
 */
export function whatsAppSendErrorFrom(err: unknown): WhatsAppSendError {
  // Il 409 è l'unico caso speciale. Tutto il resto lo sa già gestire `extractError`,
  // che estrae il titolo dell'errore Liferay: reimplementarlo qui con `err.message`
  // faceva regredire un 502 a "Request failed with status code 502", e su un `err`
  // null andava proprio in crash sull'accesso a `.response`.
  const response = (err as { response?: { status?: number; data?: unknown } })
    ?.response;

  if (response?.status !== 409 || typeof response.data !== 'object') {
    return { message: extractError(err) };
  }

  const conflict = response.data as WhatsAppWindowConflict;

  return {
    message: conflict.message ?? extractError(err),
    windowConflict: conflict,
  };
}
